import {
  Modal as ChakraUIModal,
  ModalProps as ChakraUIModalProps,
  ModalOverlay,
  ModalContent,
  ModalContentProps,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from "@chakra-ui/react";

import { MouseEventHandler, ReactNode } from "react";

import {
  Button,
  ButtonProps,
  ButtonGroup,
  ButtonGroupProps
} from "@/storybook-components";

interface TemplateModalProps
  extends Pick<ChakraUIModalProps, "isOpen" | "onClose"> {
  header: string;
  body: ReactNode;
  buttonPrimary?: string;
  buttonSecondary?: string;
  buttonPrimaryVariant?: string;
  buttonSecondaryVariant?: string;
  isFullSizedModal?: boolean;
  isPrimaryDestructive?: boolean;
  isFullWidthButtons?: boolean;
  onPrimaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
  onSecondaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
}
const defaultProps: ButtonGroupProps = {
  isAttached: false,
  isDisabled: false,
  spacing: 0
};

function TemplateModal(props: TemplateModalProps) {
  const {
    isOpen,
    onClose,
    header,
    body,
    buttonPrimary,
    buttonSecondary,
    buttonPrimaryVariant,
    buttonSecondaryVariant,
    onPrimaryClickHandler,
    onSecondaryClickHandler,
    isFullSizedModal,
    isFullWidthButtons,
    ...rest
  } = { ...defaultProps, ...props };
  const ButtonHandler = ({
    children,
    variant,
    onClick
  }: ButtonProps): JSX.Element => {
    return (
      <Button
        variant={variant}
        onClick={onClick}
        size={{ base: "lg", xl: "md" }}
        px={6}
        py={3}
        w={{ base: "100%", md: "auto" }}
        minW={{ base: "108px !important", md: "120px !important" }}
        maxW={isFullWidthButtons ? "full" : 40}
      >
        {children}
      </Button>
    );
  };

  const modalSizeProps: ModalContentProps = {
    maxW: isFullSizedModal ? undefined : 448,
    minH: isFullSizedModal ? undefined : 248,
    maxH: { base: "calc(100% - 2rem)", lg: "calc(100% - 5rem)" },
    m: isFullSizedModal ? 0 : 4,
    ...rest
  };

  return (
    <ChakraUIModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={isFullSizedModal ? "full" : "md"}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent p={6} gap={6} minW={288} boxShadow="lg" {...modalSizeProps}>
        <ModalHeader
          p={0}
          display="flex"
          fontSize="lg"
          alignItems="flex-start"
          alignSelf="stretch"
          justifyContent="space-between"
        >
          {header}
          <ModalCloseButton position="unset" w={6} h={6} />
        </ModalHeader>
        <ModalBody p={0} fontSize="md">
          {body}
        </ModalBody>
        <ModalFooter
          p={0}
          alignItems="flex-start"
          gap={6}
          alignSelf="stretch"
          justifyContent={{ base: "space-between", sm: "flex-end" }}
        >
          <ButtonGroup
            gap={4}
            justifyContent={!isFullWidthButtons ? "flex-end" : "center"}
            width={isFullWidthButtons ? "full" : "auto"}
            flexDirection={isFullWidthButtons ? "column" : "row-reverse"}
            spacing={0}
          >
            {buttonPrimary && (
              <ButtonHandler
                variant={buttonPrimaryVariant || "primary"}
                onClick={onPrimaryClickHandler}
              >
                {buttonPrimary}
              </ButtonHandler>
            )}
            {buttonSecondary && (
              <ButtonHandler
                variant={buttonSecondaryVariant || "secondary"}
                onClick={onSecondaryClickHandler}
              >
                {buttonSecondary}
              </ButtonHandler>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </ChakraUIModal>
  );
}

export { TemplateModal };
export type { TemplateModalProps };
